import React, { useState } from 'react';
import { Alert, Box, Typography } from '@mui/material';
import { PublicLocale } from './routes';
import { Photo, cardSx, colors, fonts } from './design';
import { articleCrackImages } from './articleImages';

interface DeliveryStage {
  title: string;
  work: string;
  customer: string;
  result: string;
}

const stages: Record<PublicLocale, DeliveryStage[]> = {
  sv: [
    {
      title: 'Förfrågan och avgränsning',
      work: 'Vi går igenom fastigheten, vilka ytor ni vill dokumentera och vilket beslut underlaget ska hjälpa er med. Tillsammans väljer vi Scan eller Expert och kommer överens om omfattning och offert.',
      customer: 'Beskriv fastigheten, era frågor och kända observationer. Dela ritningar eller tidigare underlag om det finns.',
      result: 'Ett överenskommet uppdrag med tydlig omfattning och leverans.',
    },
    {
      title: 'Boka och förbered',
      work: 'När uppdraget är överenskommet bokar vi ett datum och planerar flygningen. Vi stämmer av åtkomst, väderförutsättningar och relevanta tillstånd samt hur berörda behöver informeras.',
      customer: 'Utse en kontaktperson och hjälp oss med lokal information, tillträde och information till boende eller verksamheter.',
      result: 'Ett planerat genomförande. Om förutsättningarna ändras stämmer vi av en ny tid.',
    },
    {
      title: 'Flygning och bildinsamling',
      work: 'Vi flyger och samlar in översikts- och detaljbilder av de överenskomna ytorna. Bildmaterialets täckning och kvalitet behöver ge stöd för den fråga ni vill besvara.',
      customer: 'Säkerställ överenskommen åtkomst och att kontaktpersonen går att nå.',
      result: 'Bilddokumentation av de ytor som ingår i uppdraget.',
    },
    {
      title: 'Uppladdning och AI-analys',
      work: 'Vi laddar upp och strukturerar bilderna i portalen. AI-analysen identifierar synliga avvikelser som markeras och dokumenteras som fynd.',
      customer: 'Ingen egen uppladdning behövs för vår bildinsamling. Vi återkommer om något behöver kompletteras.',
      result: 'Bilder och AI-genererade observationer samlade inför granskning.',
    },
    {
      title: 'Granskning och leverans',
      work: 'Scan omfattar bilddokumentation och AI-genererade prioriteringar och förslag på nästa steg, granskade av KlaraView-teamet. Det är inte en ingenjörsbedömning. Expert tillför oberoende ingenjörsgranskning och strukturerat tekniskt underlag inför projektering och upphandling inom avtalad omfattning.',
      customer: 'Ta del av materialet och ställ frågor om observationerna och nästa steg.',
      result: 'Tillgång till bilder, dokumenterade fynd och rapporter i portalen — med Expert även ett tekniskt granskat beslutsunderlag.',
    },
    {
      title: 'Uppföljning över tid',
      work: 'En ny inspektion avtalas separat. Tidigare bilder och dokumenterade åtgärder kan ge sammanhang till nästa underlag. Monitor är ett framtida erbjudande, inte en bokningsbar tjänst idag.',
      customer: 'Använd underlaget i planeringen och kom överens med oss om nästa uppföljning när det behövs.',
      result: 'Ny dokumentation som kan ge mer sammanhang för nästa beslut. Jämförbarheten beror på materialet; dokumentation efter en åtgärd är inte i sig en verifiering av reparationen.',
    },
  ],
  en: [
    {
      title: 'Enquiry and scope',
      work: 'We discuss the property, the surfaces to document and the decision the material should support. Together we choose Scan or Expert and agree the scope and quotation.',
      customer: 'Describe the property, your questions and known observations. Share drawings or earlier documentation if available.',
      result: 'An agreed assignment with a clear scope and delivery.',
    },
    {
      title: 'Schedule and prepare',
      work: 'Once the assignment is agreed, we set a date and plan the flight. We review access, weather conditions, relevant permissions and communication with affected people.',
      customer: 'Nominate a contact person and help with local information, access and communication with residents or businesses.',
      result: 'A planned visit. If conditions change, we agree a new time.',
    },
    {
      title: 'Flight and image capture',
      work: 'We fly and collect overview and detail images of the agreed surfaces. Coverage and image quality need to support the question you want to answer.',
      customer: 'Provide the agreed access and make sure the contact person is available.',
      result: 'Image documentation of the surfaces included in the assignment.',
    },
    {
      title: 'Upload and AI analysis',
      work: 'We upload and organise the images in the portal. AI analysis identifies visible deviations that are marked and documented as findings.',
      customer: 'You do not need to upload the imagery we collect. We will contact you if additional information is needed.',
      result: 'Images and AI-generated observations brought together for review.',
    },
    {
      title: 'Review and delivery',
      work: 'Scan includes image documentation and AI-generated priorities and suggested next steps, reviewed by the KlaraView team. This is not an engineering assessment. Expert adds independent engineering review and structured technical documentation to support design and procurement within the agreed scope.',
      customer: 'Explore the material and ask questions about the observations and next steps.',
      result: 'Access to images, documented findings and reports in the portal — with Expert, also a technically reviewed decision basis.',
    },
    {
      title: 'Follow-up over time',
      work: 'A further inspection is agreed separately. Earlier images and recorded actions can provide context for the next documentation. Monitor is a future offering, not a bookable service today.',
      customer: 'Use the material in your planning and agree a further review with us when needed.',
      result: 'New documentation that can add context for the next decision. Comparability depends on the material; documentation after work does not in itself verify the repair.',
    },
  ],
};

export function DeliveryProcess({ locale = 'sv' }: { locale?: PublicLocale }) {
  const en = locale === 'en';
  const [imageFailed, setImageFailed] = useState(false);
  return <>
    <Box component="ol" role="list" aria-label={en ? 'From enquiry to follow-up' : 'Från förfrågan till uppföljning'}
      sx={{ m: 0, p: 0, listStyle: 'none', display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' }, gap: 2 }}>
      {stages[locale].map((stage, index) => <Box component="li" key={stage.title} sx={{ ...cardSx, display: 'flex', flexDirection: 'column' }}>
        <Typography sx={{ fontFamily: fonts.mono, fontSize: 12, color: colors.rust, mb: 2 }}>{String(index + 1).padStart(2, '0')}</Typography>
        <Typography variant="h3" sx={{ fontSize: { xs: 25, md: 29 }, mb: 2 }}>{stage.title}</Typography>
        <Typography sx={{ color: colors.muted, mb: 3 }}>{stage.work}</Typography>
        <Box sx={{ borderTop: `1px solid ${colors.line}`, pt: 2, mt: 'auto' }}>
          <Typography sx={{ fontSize: 12, fontFamily: fonts.mono, fontWeight: 700, mb: 1 }}>{en ? 'Your part' : 'Er del'}</Typography>
          <Typography sx={{ color: colors.muted, fontSize: 14, mb: 2 }}>{stage.customer}</Typography>
          <Typography sx={{ fontSize: 12, fontFamily: fonts.mono, fontWeight: 700, mb: 1 }}>{en ? 'What you receive' : 'Det ni får'}</Typography>
          <Typography sx={{ fontSize: 14 }}>{stage.result}</Typography>
        </Box>
      </Box>)}
    </Box>
    <Box sx={{ mt: 5, display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3, alignItems: 'start' }}>
      <Box sx={{ '& img': { height: { xs: 280, md: 330 }, objectPosition: 'center 65%' } }}>
        <Photo image="pilot" locale={locale} />
      </Box>
      <Box component="figure" sx={{ m: 0, '& img': { display: 'block', width: '100%', height: 'auto' } }}>
        {imageFailed
          ? <Alert severity="warning">{en ? 'The portal illustration could not be loaded.' : 'Portalbilden kunde inte laddas.'}</Alert>
          : <img src={articleCrackImages.preview} width={1517} height={832} loading="lazy"
            alt={en ? 'Example of an AI-marked observation in the KlaraView portal' : 'Exempel på en AI-markerad observation i KlaraViews portal'}
            onError={() => setImageFailed(true)} />}
        <Typography component="figcaption" sx={{ color: colors.muted, fontSize: 12, mt: 1.5 }}>
          {en ? 'From image to observation. The percentages in this example show model confidence, not engineering-assessed severity.' : 'Från bild till observation. Procenttalen i exemplet visar modellens säkerhet, inte ingenjörsbedömd allvarlighetsgrad.'}
        </Typography>
      </Box>
    </Box>
  </>;
}
